import { Box, List } from '@mui/material';
import { ReactNode } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { Filter } from '../page-component';
import { getFilterProps, inputLookUp } from './utils';
import { FavoritesToggle } from '../page-component/pageStyles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const FilterList = ({
  control,
  errors,
  filters,
  currentFormValues,
  showFilters,
  setShowFilters,
  renderAfter,
}: {
  control: Control;
  errors: FieldErrors;
  filters: {
    label: string;
    data: Filter[];
  }[];
  currentFormValues: Record<string, unknown>;
  showFilters?: Record<string, boolean>;
  setShowFilters?: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  /** Optional custom content injected immediately after a specific filter,
   *  keyed by the filter's `id`. Renders in the natural flow of the filter
   *  list (e.g. the Resources attribute-mode toggle after Employment Type).
   *  Filters whose id is not present are unaffected. */
  renderAfter?: Record<string, ReactNode>;
}) => {
  const renderFilters = (control: Control, paramFilters?: Filter[]) => {
    const arrayFilters = paramFilters;

    return arrayFilters?.flatMap((filter: Filter, index: number) => {
      if (!filter?.type || filter?.isVirtual) return [];

      const Component = inputLookUp[filter?.type || 'select'];
      const SubComponent =
        filter.conditionalRender && filter?.conditionalRender?.type
          ? inputLookUp[filter?.conditionalRender?.type]
          : undefined;

      const props = getFilterProps(filter, index, control, errors);

      const afterContent = renderAfter?.[filter.id];

      return [
        <div
          style={{ margin: '10px 0', padding: '0 10px' }}
          key={`${index}_${filter.id}`}
          data-testid={`filter-container-${filter.name}`}
        >
          <Component {...props} />
          {SubComponent &&
            filter.conditionalRender &&
            currentFormValues[filter.id] ===
              filter?.conditionalRender?.parentEquals && (
              <SubComponent
                {...getFilterProps(
                  filter.conditionalRender,
                  filter.conditionalRender.id,
                  control,
                  errors
                )}
              />
            )}
        </div>,
        ...(afterContent
          ? [
              <div
                style={{ margin: '10px 0', padding: '0 10px' }}
                key={`${index}_${filter.id}_after`}
                data-testid={`filter-after-${filter.name}`}
              >
                {afterContent}
              </div>,
            ]
          : []),
      ];
    });
  };

  const formatLabel = (label: string) => {
    return label.toLowerCase().replace(/ /g, '_');
  };

  return (
    <List
      sx={{ width: '100%', bgcolor: 'background.paper' }}
      component="nav"
      aria-labelledby="nested-list-subheader"
      id="filters-list"
    >
      {filters?.map(
        (filter: { label: string; data?: Filter[] }, index: number) => {
          return (
            <Box>
              <Box>
                <FavoritesToggle
                  onClick={() => {
                    if (!setShowFilters) return;
                    const showState = showFilters || {};
                    showState[formatLabel(filter.label)] =
                      !showState?.[formatLabel(filter.label)];
                    setShowFilters({ ...showState });
                  }}
                  data-testid="filters-favourites-toggle"
                >
                  <p style={{ margin: 0 }}>{filter.label}</p>
                  {showFilters?.[formatLabel(filter.label)] ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </FavoritesToggle>
                {showFilters?.[formatLabel(filter.label)] &&
                  renderFilters(control, filter.data)}
              </Box>
            </Box>
          );
        }
      )}
    </List>
  );
};
